<template>
    <div id="container" v-bind:class="getContainerClass()">
        <div id="map" v-bind:class="this.mapStyleClass" ref="map" width="100%" height="100%" v-on:click="onMapClicked()"></div>
        <a v-if="!this.hideOndagoLogo" href="https://www.ondagoapp.com" target="_blank">
            <img id="ondagoLogo" class="ondago-logo-full" src="@/assets/ondago_logo_gradient.png" />
            <img id="ondagoLogo" class="ondago-logo-pin" src="@/assets/logo.png" />
        </a>
        <div id="searchBar" v-if="!this.disableSearch">
            <a href="#" v-on:click="onSearchClicked"><img src="@/assets/buttons/searchActionButton.png" /></a>
        </div>
        <div id="rightMiddleActionBar">
            <a href="#" v-on:click="onZoomInClicked()"><img src="@/assets/buttons/zoomInActionButton.png" /></a>
            <a href="#" v-on:click="onZoomOutClicked()"><img src="@/assets/buttons/zoomOutActionButton.png" /></a>
        </div>
        <div id="bottomRightActionBar">
            <a href="#" v-if="layersButtonVisible" v-on:click="onLayersClicked()"><img src="@/assets/buttons/layersButton.png" /></a>
        </div>
        <div id="attributions" v-html="attributionsHTML"></div>
        <transition name="slide-fade-from-left">
            <MapSearchPanel id="searchPanel" v-if="(!this.disableLeftPanel && searchPanelVisible) || this.searchAlwaysVisible" v-bind:searchableObjects="this.searchableObjects" 
                v-bind:mapObjectStatusTemplates="this.mapObjectStatusTemplates"
                v-bind:allMapObjectStatus="this.allMapObjectStatus" 
                v-on:searchable-object-selected="onSearchableObjectSelected"></MapSearchPanel>
        </transition>
        <transition name="slide-fade-from-left">
            <MapElementDetailPanel id="detailPanel" v-if="!this.disableLeftPanel && detailPanelVisible" v-bind:detailSpec="selectedMapElementDetailSpec"></MapElementDetailPanel>
        </transition>
        <transition name="slide-fade-from-right">
            <MapLayersPanel id="layersPanel" v-if="layersPanelVisible" v-bind:mapLayers="this.mapSpec.mapLayers"></MapLayersPanel>
        </transition>
    </div>
</template>

<script>
import MapSearchPanel from './MapSearchPanel.vue'
import MapElementDetailPanel from './MapElementDetailPanel.vue'
import MapLayersPanel from './MapLayersPanel.vue'
import MapSpec from "@/map/MapSpec"
import MapboxMapControl from "@/ui/map/MapboxMapControl"
import MapElementDataStream from '@/map/stream/MapElementDataStream'
import Annotation from '@/map/element/Annotation'
import MapElementDetailSpec from '@/map/detail/MapElementDetailSpec'

export default {
    components: {
        MapSearchPanel,
        MapElementDetailPanel,
        MapLayersPanel
    },
    name: "Map",
    data: () => {
        return {
            mapControl: null,
            searchPanelVisible: false,
            detailPanelVisible: false,
            layersPanelVisible: false,
            layersButtonVisible: false,
            attributionsHTML: null,
            selectedMapElement: null,
            highlightedMapElements: [],
            allMapObjectStatus: [],
            mapObjectStatusTemplates: []
        }
    },
    props: {
        mapSpec: MapSpec,
        disableScrollZoom: Boolean,
        disableSearch: Boolean,
        disableLeftPanel: Boolean,
        hideOndagoLogo: Boolean,
        searchAlwaysVisible: Boolean
    },
    computed: {
        mapStyleClass() {
            if (this.searchAlwaysVisible) {
                return "search-always-opened-map";
            } else {
                return "";
            }
        },

        searchableObjects() {
            return this.mapSpec ? this.mapSpec.searchableObjects : [];
        },

        selectedMapElementDetailSpec() {
            if (this.selectedMapElement) {
                return new MapElementDetailSpec(this.selectedMapElement, this.mapSpec.mapBundleId)
            }
            return null;
        }
    },
    watch: {
        mapSpec: function() {
            this.$forceUpdate();
        }
    },
    methods: {
        getContainerClass() {
            if ((!this.disableLeftPanel && this.searchPanelVisible) || (!this.disableLeftPanel && this.detailPanelVisible)) {
                return "opened-left-panel";
            } else if (this.layersPanelVisible) {
                return "opened-right-panel";
            } else {
                return "closed-left-panel closed-right-panel";
            }
        },

        refresh() {
            if (this.mapSpec) {
                this.mapControl = new MapboxMapControl(this.mapSpec, this.$refs["map"]);
                this.mapControl.onClick((selectedFeatures) => {
                    this.onFeaturesSelected(selectedFeatures);
                });
                this.mapControl.onMouseMove((mouseOverFeatures) => {
                    this.onMouseOverFeatures(mouseOverFeatures);
                });
                this.mapControl.load();

                if (this.disableScrollZoom) {
                    this.mapControl.disableScrollZoom();
                } else {
                    this.mapControl.enableScrollZoom();
                }

                this.layersButtonVisible = this.mapSpec.mapLayers.length > 0;
                
                this.attributionsHTML = this.mapSpec.attributionsHTML;

                this.mapSpec.mapObjectStatusDataStream.observe().subscribe((data) => {
                    if (data) {
                        this.mapObjectStatusTemplates = data.mapObjectStatusTemplates;
                        this.allMapObjectStatus = data.mapObjectStatusList;
                        this.$forceUpdate();
                    }
                });
            }
        },

        onZoomInClicked() {
            this.mapControl.zoomIn();
        },

        onZoomOutClicked() {
            this.mapControl.zoomOut();
        },

        onLayersClicked() {
            this.searchPanelVisible = false;
            this.layersPanelVisible = true;
        },

        onSearchClicked() {
            this.layersPanelVisible = false;
            this.searchPanelVisible = true;
        },

        onMapClicked() {
            this.searchPanelVisible = false;
            this.layersPanelVisible = false;
        },

        onFeaturesSelected(features) {
            var selectedFeature = null;
            for (const feature of features) {
                if (feature.properties.interactive) {
                    if (feature.properties.type == "annotation") {
                        selectedFeature = feature;
                        break;
                    } else {
                        selectedFeature = feature;
                    }
                }
            }

            this.selectMapElement(selectedFeature ? selectedFeature.properties.id : null, false, true);
        },

        onSearchableObjectSelected(searchableObjectId) {
            this.selectMapElement(searchableObjectId, true);
        },

        onMouseOverFeatures(mouseOverFeatures) {
            const hoveredMapElements = [];
            var hasAnnotations = false;
            for (const feature of mouseOverFeatures) {
                const mapElement = this._findMapElementById(feature.properties.id);
                if (mapElement && mapElement.isInteractive()) {
                    hoveredMapElements.push(mapElement);
                    if (mapElement instanceof Annotation) {
                        hasAnnotations = true;
                    }
                }
            }

            this.highlightedMapElements = this.highlightedMapElements.filter((element) => {
                if (element instanceof Annotation || !hasAnnotations) {
                    for (const feature of mouseOverFeatures) {
                        if (element.getId() == feature.properties.id) {
                            return true;
                        }
                    }
                }

                element.setHighlighted(false);

                return false;
            });

            if (hoveredMapElements.length > 0) {
                this.mapControl.changeMouseCursor("pointer");
            } else {
                this.mapControl.changeMouseCursor("");
            }

            for (const mapElement of hoveredMapElements) {
                if (mapElement instanceof Annotation || hasAnnotations == false) {
                    mapElement.setHighlighted(true);
                    this.highlightedMapElements.push(mapElement);
                }
            }
        },

        selectMapElement(mapElementId, centerOnMapElement, fireEvent = true, forceSelect = true) {
            if (mapElementId) {
                this.searchPanelVisible = false;

                var mapElement = this._findMapElementById(mapElementId);
                if (mapElement.masterMapElement) {
                    mapElement = mapElement.masterMapElement;
                }
                if (mapElement && (forceSelect || mapElement.isInteractive())) {
                    if (this.selectedMapElement != null) {
                        this.selectedMapElement.setSelected(false);
                        this.mapControl.hideSelectionMarker();
                    }

                    this._deepSetMapElementSelected(mapElement, true);

                    if (fireEvent) {
                        this.$root.$emit("onMapElementSelected", { id: mapElement.id });
                        
                        var event = {
                            type: "objectSelected",
                            id: mapElement.id
                        }
                        if (window.parent) {
                            window.parent.postMessage(event, "*");
                        }
                    }
                    this.selectedMapElement = mapElement;

                    if (centerOnMapElement) {
                        this.mapControl.centerOnMapElement(mapElement);
                    }

                    if (mapElement instanceof Annotation) {
                        this.mapControl.showSelectionMarkerForMapElement(mapElement);
                    }

                    this.detailPanelVisible = true;
                    this.layersPanelVisible = false;
                }
            } else {
                if (this.selectedMapElement != null) {
                    this._deepSetMapElementSelected(this.selectedMapElement, false);
                }

                this.mapControl.hideSelectionMarker();
                
                this.detailPanelVisible = false;
                this.layersPanelVisible = false;
            }
        },

        setMapElementVisibility(mapElementId, visible) {
            if (mapElementId) {
                const mapElement = this._findMapElementById(mapElementId);
                if (mapElement) {
                    mapElement.setHidden(!visible);
                }
            }
        },

        _findMapElementById(mapElementId) {
            for (const dataStream of this.mapSpec.dataStreams) {
                if (dataStream instanceof MapElementDataStream) {
                    for (const mapElement of dataStream.mapElements) {
                        if (mapElement.getId() == mapElementId) {
                            return mapElement;
                        }
                    }
                }
            }

            return null;
        },

        _deepSetMapElementSelected(mapElement, selected) {
            mapElement.setSelected(selected);
            if (mapElement.children && mapElement.children.length > 0) {
                for (var child of mapElement.children) {
                    this._deepSetMapElementSelected(child, selected);
                }
            }
        }
    }
}
</script>

<style>
    .mapboxgl-ctrl-group {
        background: transparent !important;
        box-shadow: none !important;
    }

    .mapboxgl-ctrl-bottom-right {
        bottom: 30px !important;
    }

    .mapboxgl-ctrl-geolocate {
        background: transparent !important;
        background-image: url(~@/assets/buttons/userLocationLockedActionButton.png) !important;
        background-size: 50px !important;
        width: 50px !important;
        height: 50px !important;
        background-color: transparent !important;
        background-repeat: no-repeat !important;
        border: none !important;
    }
</style>

<style lang="scss" scoped>

#container {
    position: relative;
    width: 100%;
    height: 100%;
}

.search-always-opened-map {
    margin-left: 350px;
}

// .opened-left-panel {
//     transition: all 0.6s;
//     padding-left: 350px;
// }

.closed-left-panel {
    transition: all 0.5s;
    padding-left: 0px;
}

.opened-right-panel {
    transition: all 0.6s;
    padding-right: 0px;
}

.closed-right-panel {
    transition: all 0.5s;
    padding-right: 0px;
}

.ondago-logo-pin {
    display: none;
}

#map {
    height: 100%;
}

#searchBar {
    position: absolute;
    top: 0%;
    left: 0%;
    margin-top: 10px;
    margin-left: 10px;
}

#searchPanel {
    background-color: #ffffff;
    box-shadow: 5px 0px #88888855;
    position: absolute;
    top: 0%;
    left: 0%;
    width: 350px;
    height: 100%;
}

#detailPanel {
    background-color: #ffffff;
    box-shadow: 5px 0px #88888855;
    position: absolute;
    top: 0%;
    left: 0%;
    width: 350px;
    height: 100%;
}

#layersPanel {
    background-color: #ffffff;
    box-shadow: 5px 0px #88888855;
    position: absolute;
    top: 0%;
    right: 0%;
    width: 350px;
    height: 100%;
}

#rightMiddleActionBar {
    position: absolute;
    top: 50%;
    left: 100%;
    margin-left: -60px;
    margin-top: -50px;
    width: 50px;
}

#rightMiddleActionBar a img {
    width: 50px;
    height: 50px;
}

#bottomRightActionBar {
    position: absolute;
    bottom: 50px;
    right: 10px;
}

#bottomRightActionBar img {
    width: 50px;
    height: 50px;
}

#ondagoLogo {
    position: absolute;
    top: 100%;
    left: 0%;
    margin-top: -40px;
    margin-left: 10px;
    height: 30px;
}

#attributions {
    position: absolute;
    top: 100%;
    right: 0%;
    margin-top: -40px;
    margin-right: 10px;
}

.slide-fade-from-left-enter-active {
  transition: all 0.5s ease;
}
.slide-fade-from-left-leave-active {
  transition: all 0.5s ease;
}
.slide-fade-from-left-enter, .slide-fade-from-left-leave-to
/* .slide-fade-from-left-leave-active below version 2.1.8 */ {
  transform: translateX(-350px);
}

.slide-fade-from-right-enter-active {
  transition: all 0.5s ease;
}
.slide-fade-from-right-leave-active {
  transition: all 0.5s ease;
}
.slide-fade-from-right-enter, .slide-fade-from-right-leave-to
/* .slide-fade-from-right-leave-active below version 2.1.8 */ {
  transform: translateX(350px);
}

@media only screen and (max-width: 600px) {
    .ondago-logo-full {
        display: none;
    }

    .ondago-logo-pin {
        display: inline;
    }

    #rightMiddleActionBar {
        display: none;
    }

    .slide-fade-from-left-enter, .slide-fade-from-left-leave-to {
        transform: translateX(-80%);
    }

    .slide-fade-from-right-enter, .slide-fade-from-right-leave-to {
        transform: translateX(80%);
    }

    #searchPanel {
        width: 80%;
    }
    
    #detailPanel {
        width: 80%;
    }

    .opened-left-panel {
        padding-left: 0px;
    }
}
</style>